// src/EmployeeList.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import EmployeeForm from './EmployeeForm.jsx';
import { api } from './lib/apiFetch';

function displayName(emp = {}) {
  const pf = (emp.preferredFirst || '').trim();
  const pl = (emp.preferredLast || '').trim();
  const f  = (emp.firstName || '').trim();
  const l  = (emp.lastName || '').trim();
  if (pl || pf) return `${pl || l}, ${pf || f}`.replace(/^,\s*|,\s*$/g, '');
  return `${l}, ${f}`.replace(/^,\s*|,\s*$/g, '');
}

export default function EmployeeList() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let resp = await api.get('/employees');

      // If a Fetch Response, parse JSON
      if (resp && typeof resp.json === 'function') {
        resp = await resp.json();
      }

      // If stringified JSON, parse
      let data = resp;
      if (typeof data === 'string') {
        data = JSON.parse(data);
      }

      // Unwrap common shapes
      if (!Array.isArray(data)) {
        data = data?.data ?? data?.items ?? data?.Items ?? [];
      }

      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError(String(e?.message || e) || 'Failed to load employees.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
    setSortConfig({ key, direction });
  };

  const handleRowClick = (emp) => {
    if (selected) return;
    setSelected({ ...emp, _new: false });
  };

  const handleAddEmployee = () => {
    if (selected) return;
    setSelected({
      _new: true,
      id: undefined,
      empId: '',
      firstName: '',
      lastName: '',
      preferredFirst: '',
      preferredLast: '',
      email: '',
      phone: '',
      empPosition: 'driver',
      status: 'active',
      startDate: '',
    });
  };

  const handleSaved = async () => {
    setSelected(null);
    await fetchEmployees();
  };

  const handleCancelEdit = async () => {
    setSelected(null);
    await fetchEmployees();
  };

  const sortedFiltered = useMemo(() => {
    let items = [...rows];

    if (!includeInactive) {
      items = items.filter((e) => (e.status || 'active').toLowerCase() !== 'inactive');
    }

    items.sort((a, b) => {
      const dir = sortConfig.direction === 'ascending' ? 1 : -1;
      const key = sortConfig.key;

      if (key === 'name') {
        const an = ((a.preferredLast || a.lastName || '') + ',' + (a.preferredFirst || a.firstName || '')).toLowerCase();
        const bn = ((b.preferredLast || b.lastName || '') + ',' + (b.preferredFirst || b.firstName || '')).toLowerCase();
        if (an < bn) return -1 * dir;
        if (an > bn) return  1 * dir;
        return 0;
      }

      const av = (a[key] ?? '').toString().toLowerCase();
      const bv = (b[key] ?? '').toString().toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return items;

    return items.filter((e) =>
      [
        e.firstName, e.lastName, e.preferredFirst, e.preferredLast,
        e.email, e.phone, e.empPosition, e.status,
      ]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q))
    );
  }, [rows, sortConfig, searchTerm, includeInactive]);

  const renderDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return isNaN(date) ? d : date.toLocaleDateString();
  };

  return (
    <div className="container listDiv">
      <div className="search-and-add-bar" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Include Inactive
        </label>
        <button type="button" onClick={handleAddEmployee} disabled={!!selected}>
          Add Employee
        </button>
        <button type="button" onClick={fetchEmployees} disabled={loading || !!selected}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div style={{ color: 'crimson', margin: '8px 0' }}>{error}</div>}

      <div className="table-container">
        {loading ? (
          <div>Loading…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="styleCol1" onClick={() => requestSort('name')}>Name</th>
                <th className="styleCol1" onClick={() => requestSort('email')}>Email</th>
                <th className="styleCol1" onClick={() => requestSort('phone')}>Phone</th>
                <th className="styleCol2" onClick={() => requestSort('empPosition')}>Position</th>
                <th className="styleCol1" onClick={() => requestSort('status')}>Status</th>
                <th className="styleCol1" onClick={() => requestSort('startDate')}>Start</th>
              </tr>
            </thead>
            <tbody className="scrollable-tbody">
              {sortedFiltered.map((emp) => (
                <tr
                  key={emp.id || emp.empId}
                  onClick={() => handleRowClick(emp)}
                  style={{
                    cursor: selected ? 'not-allowed' : 'pointer',
                    pointerEvents: selected ? 'none' : 'auto',
                  }}
                  aria-disabled={selected ? 'true' : 'false'}
                >
                  <td className="styleCol1">{displayName(emp)}</td>
                  <td className="styleCol1">{emp.email || '—'}</td>
                  <td className="styleCol1">{emp.phone || '—'}</td>
                  <td className="styleCol2">{emp.empPosition || 'driver'}</td>
                  <td className="styleCol1">{emp.status || 'active'}</td>
                  <td className="styleCol1">{renderDate(emp.startDate)}</td>
                </tr>
              ))}
              {!sortedFiltered.length && (
                <tr><td colSpan={6} style={{ color: '#666' }}>No matches</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="container formDiv">
          <EmployeeForm
            employee={selected}
            onCancelEdit={handleCancelEdit}
            onSuccessfulSubmit={handleSaved}
          />
        </div>
      )}
    </div>
  );
}
